/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useState, useRef, JSX } from 'react';
import { FilterItem } from '@wconcept/wconcept-common/models/renewal/common';
import Box from '@wconcept/wconcept-ui-renewal/components/atoms/Box';
import Input from '@wconcept/wconcept-ui-renewal/components/atoms/Input';
import Tag from '@wconcept/wconcept-ui-renewal/components/atoms/Tag';
import {
  Tabs,
  TabPanel,
} from '@wconcept/wconcept-ui-renewal/components/molecules/Tabs';
import FltChkItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/FltChkItem';
import { Virtuoso } from 'react-virtuoso';
import FltBrandBox from '../../_list/FltBrandBox';

function BrandContent({
  selectedValue,
  handleTabsBrandChange,
  topBrands,
  newBrands,
  brands,
  isItemSelected,
  onFilterSelect,
  brandTabs,
  selectedFilters,
  noListMsg,
  noSearchMsg,
  hideFilterOptionCounts,
}: {
  selectedValue: string;
  handleTabsBrandChange: (value: string) => void;
  topBrands?: FilterItem[];
  newBrands?: FilterItem[];
  brands?: FilterItem[];
  isItemSelected: (filterType: string[], item: FilterItem) => boolean;
  brandTabs: string[];
  selectedFilters: FilterItem[];
  onFilterSelect: (
    filterType: string,
    item: FilterItem,
    isSelected: boolean
  ) => void;
  noListMsg?: JSX.Element;
  noSearchMsg?: JSX.Element;
  hideFilterOptionCounts?: boolean;
}) {
  // 브랜드 리스트 상태 관리
  const [brandList, setBrandList] = useState<FilterItem[] | undefined>(
    undefined
  );
  const [topBrandList, setTopBrandList] = useState<FilterItem[] | undefined>(
    undefined
  );
  const [newBrandList, setNewBrandList] = useState<FilterItem[] | undefined>(
    undefined
  );
  // (전체) 브랜드 검색명
  const [searchBrandName, setSearchBrandName] = useState<string | undefined>(
    undefined
  );

  // 초기 로드 상태 관리
  const isInitialLoadRef = useRef({
    brand: false,
    topBrand: false,
    newBrand: false,
  });

  // 원본 리스트 참조 저장
  const originalListsRef = useRef({
    brand: brands,
    topBrand: topBrands,
    newBrand: newBrands,
  });

  // 브랜드 리스트 업데이트 함수
  const updateBrandList = useCallback(
    (
      originalList?: FilterItem[],
      filterTypes?: string[],
      tabType?: 'brand' | 'topBrand' | 'newBrand'
    ) => {
      if (originalList && selectedFilters) {
        // 선택된 필터 중 현재 탭에 해당하는 브랜드만 필터링
        const selectedBrands = selectedFilters.filter(item =>
          filterTypes?.includes(item.filterType as string)
        );

        // 원본 리스트에 없는 선택된 브랜드 찾기
        const missingBrands = selectedBrands
          .filter(
            selected =>
              !originalList.some(brand => brand.code === selected.code)
          )
          .map(item => ({ ...item, count: 0 }));

        if (tabType && !isInitialLoadRef.current[tabType]) {
          // 선택된 항목과 선택되지 않은 항목을 분리
          const [selectedItems, unselectedItems] = originalList.reduce(
            ([selected, unselected]: [FilterItem[], FilterItem[]], item) => {
              if (filterTypes && isItemSelected(filterTypes, item)) {
                selected.push(item);
              } else {
                unselected.push(item);
              }
              return [selected, unselected];
            },
            [[], []]
          );
          // 누락된 브랜드, 선택된 항목, 선택되지 않은 항목 순으로 정렬하여 반환
          return [...missingBrands, ...selectedItems, ...unselectedItems];
        }
        // 초기 로드가 아닌 경우, 누락된 브랜드를 원본 리스트 앞에 추가
        return [...missingBrands, ...originalList];
      }

      // originalList가 없거나 selectedFilters가 없는 경우 원본 리스트 그대로 반환
      return originalList;
    },
    [selectedFilters, isItemSelected]
  );

  // 컴포넌트 마운트 시 브랜드 리스트 초기화
  useEffect(() => {
    // 브랜드 검색 후 체크 시 초기화되는 현상 막기
    if (searchBrandName) return;
    if (brands !== undefined) {
      setBrandList(
        updateBrandList(brands, ['brand', 'topBrand', 'newBrand'], 'brand')
      );
      originalListsRef.current.brand = brands;
      isInitialLoadRef.current.brand = true;
    }
  }, [brands, searchBrandName, updateBrandList]);

  useEffect(() => {
    if (topBrands && selectedValue === 'topBrand') {
      setTopBrandList(updateBrandList(topBrands, ['topBrand'], 'topBrand'));
      originalListsRef.current.topBrand = topBrands;
      isInitialLoadRef.current.topBrand = true;
    }
  }, [topBrands]);

  useEffect(() => {
    if (newBrands && selectedValue === 'newBrand') {
      setNewBrandList(updateBrandList(newBrands, ['newBrand'], 'newBrand'));
      originalListsRef.current.newBrand = newBrands;
      isInitialLoadRef.current.newBrand = true;
    }
  }, [newBrands]);

  // 탭 변경 처리 함수
  const handleTabChange = useCallback(
    (value: string) => {
      isInitialLoadRef.current[value as keyof typeof isInitialLoadRef.current] =
        true;

      handleTabsBrandChange(value);

      switch (value) {
        case 'brand':
          // 브랜드 검색 후 탭 변경 시 초기화되는 현상 막기
          if (searchBrandName) return;
          setBrandList(
            updateBrandList(
              originalListsRef?.current?.brand,
              ['brand', 'topBrand', 'newBrand'],
              'brand'
            )
          );
          break;
        case 'topBrand':
          setTopBrandList(
            updateBrandList(
              originalListsRef?.current?.topBrand,
              ['topBrand'],
              'topBrand'
            )
          );
          break;
        case 'newBrand':
          setNewBrandList(
            updateBrandList(
              originalListsRef?.current?.newBrand,
              ['newBrand'],
              'newBrand'
            )
          );
          break;
        default:
          break;
      }
    },
    [updateBrandList, handleTabsBrandChange]
  );

  // 필터 선택 처리 함수
  const handleFilterSelect = useCallback(
    (type: string, item: FilterItem, isSelected: boolean) => {
      const selectedBrandList = selectedFilters.filter(
        v =>
          v.filterType === 'brand' ||
          v.filterType === 'topBrand' ||
          v.filterType === 'newBrand'
      );
      if (selectedBrandList.length > 4 && isSelected) {
        alert('브랜드는 최대 5개까지만 선택 가능합니다.');
        return;
      }
      onFilterSelect(type, item, isSelected);

      isInitialLoadRef.current[type as keyof typeof isInitialLoadRef.current] =
        true;

      switch (type) {
        case 'brand':
          setBrandList(prevList =>
            prevList?.map(brand =>
              brand.code === item.code
                ? { ...brand, selected: isSelected }
                : brand
            )
          );
          break;
        case 'topBrand':
          setTopBrandList(prevList =>
            prevList?.map(brand =>
              brand.code === item.code
                ? { ...brand, selected: isSelected }
                : brand
            )
          );
          break;
        case 'newBrand':
          setNewBrandList(prevList =>
            prevList?.map(brand =>
              brand.code === item.code
                ? { ...brand, selected: isSelected }
                : brand
            )
          );
          break;
        default:
          break;
      }
    },
    [onFilterSelect, selectedFilters]
  );

  // 검색 중이 아닐 때는 API 응답(brands)을 우선 사용해 agg 재조회 직후 state 불일치 방지
  const allBrandList = searchBrandName ? brandList : brands ?? brandList;

  const renderBrandItem = useCallback(
    (brand: FilterItem, type?: string) => {
      if (!brand?.code) {
        return null;
      }
      const filterTypes = ['brand', 'topBrand', 'newBrand'];
      const checked = isItemSelected(filterTypes, brand);

      return (
        <FltChkItem
          value={brand.code}
          checked={checked}
          name={brand.name}
          cnt={hideFilterOptionCounts ? undefined : brand.count}
          suffix={
            brand.tags?.includes('단독') ? (
              <Tag className="tag-exclusive">단독</Tag>
            ) : null
          }
          onChange={e => {
            e.preventDefault();
            if (type) {
              handleFilterSelect(type, brand, !checked);
            }
          }}
        />
      );
    },
    [hideFilterOptionCounts, isItemSelected, handleFilterSelect]
  );

  // 브랜드 아이템 목록 렌더링 함수
  const renderBrandItems = useCallback(
    (list?: FilterItem[], type?: string, emptyContent?: JSX.Element) => {
      if (list && list.length > 0) {
        return (
          <Virtuoso
            key={`${type}-${list.length}-${list[0]?.code ?? ''}-${
              list[list.length - 1]?.code ?? ''
            }`}
            data={list}
            defaultItemHeight={36}
            itemContent={(_index, brand) => renderBrandItem(brand, type)}
            components={{
              List: props => <div {...props} />,
            }}
            overscan={{ main: 100, reverse: 100 }}
            increaseViewportBy={{ top: 200, bottom: 200 }}
            initialItemCount={Math.min(20, list.length)}
            computeItemKey={(_index, brand) => brand.code ?? _index}
          />
        );
      }
      return list ? emptyContent : null;
    },
    [renderBrandItem]
  );

  // 컴포넌트 렌더링
  return (
    <Box className="coner-brand">
      <FltBrandBox>
        <Tabs
          selectedValue={selectedValue}
          onChange={handleTabChange}
          className="tabs-brand"
        >
          {brandTabs.map(subTypeValue => {
            if (subTypeValue === 'brand') {
              return (
                <TabPanel key="brand" label="전체" value="brand">
                  <Input
                    placeholder="브랜드명을 입력해 주세요"
                    allClear
                    searchBtn
                    value={searchBrandName}
                    onPressEnter={e => {
                      const inputValue = (e.target as HTMLInputElement).value;
                      setSearchBrandName(inputValue);
                      setBrandList(
                        brands?.filter(v => v?.name?.includes(inputValue)) ?? []
                      );
                    }}
                  />
                  {renderBrandItems(
                    allBrandList,
                    'brand',
                    searchBrandName ? noSearchMsg : noListMsg
                  )}
                </TabPanel>
              );
            }
            if (subTypeValue === 'topBrand') {
              return (
                <TabPanel key="topBrand" label="인기" value="topBrand">
                  {topBrandList?.length ? (
                    <ul>
                      {topBrandList.map(brand => (
                        <FltChkItem
                          key={brand.code}
                          value={brand.code}
                          checked={isItemSelected?.(
                            ['brand', 'topBrand', 'newBrand'],
                            brand
                          )}
                          name={brand.name}
                          cnt={hideFilterOptionCounts ? undefined : brand.count}
                          suffix={
                            brand.tags?.includes('단독') ? (
                              <Tag className="tag-exclusive">단독</Tag>
                            ) : null
                          }
                          onChange={e => {
                            e.preventDefault();
                            handleFilterSelect(
                              'topBrand',
                              brand,
                              !isItemSelected?.(
                                ['brand', 'topBrand', 'newBrand'],
                                brand
                              )
                            );
                          }}
                        />
                      ))}
                    </ul>
                  ) : null}
                  {topBrandList?.length === 0 ? noListMsg : null}
                </TabPanel>
              );
            }
            if (subTypeValue === 'newBrand') {
              return (
                <TabPanel key="newBrand" label="신규" value="newBrand">
                  {newBrandList?.length ? (
                    <ul>
                      {newBrandList.map(brand => (
                        <FltChkItem
                          key={brand.code}
                          value={brand.code}
                          checked={isItemSelected?.(
                            ['brand', 'topBrand', 'newBrand'],
                            brand
                          )}
                          name={brand.name}
                          cnt={hideFilterOptionCounts ? undefined : brand.count}
                          suffix={
                            brand.tags?.includes('단독') ? (
                              <Tag className="tag-exclusive">단독</Tag>
                            ) : null
                          }
                          onChange={e => {
                            e.preventDefault();
                            handleFilterSelect(
                              'newBrand',
                              brand,
                              !isItemSelected?.(
                                ['brand', 'topBrand', 'newBrand'],
                                brand
                              )
                            );
                          }}
                        />
                      ))}
                    </ul>
                  ) : null}
                  {newBrandList?.length === 0 ? noListMsg : null}
                </TabPanel>
              );
            }
            return null;
          })}
        </Tabs>
      </FltBrandBox>
    </Box>
  );
}

export default BrandContent;
